import { trackPhoneCall } from "@/lib/googleAds";
import { Link } from "wouter";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { CanonicalTag } from "@/components/CanonicalTag";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";
import { useEffect } from "react";
import { getMetaKeywords } from "@/data/metaKeywords";
import GoogleReviews from "@/components/GoogleReviews";
import { OpenGraphTags } from "@/components/OpenGraphTags";
import { 
  Paintbrush, 
  Home as HomeIcon, 
  Shield, 
  Droplets, 
  Waves, 
  Hammer, 
  Wrench, 
  Sparkles,
  Building2,
  Palette,
  Layers
} from "lucide-react";

const services = [
  {
    slug: "cabinet-painting",
    title: "Cabinet Painting",
    description: "Expert kitchen and bathroom cabinet refinishing and repainting services",
    icon: Sparkles,
  },
  {
    slug: "interior-exterior-painting",
    title: "Interior & Exterior Painting",
    description: "Professional interior and exterior painting services for walls, ceilings, and trim",
    icon: Paintbrush,
  },
  {
    slug: "exterior-painting",
    title: "Exterior Painting",
    description: "High-quality exterior painting to protect and beautify your property",
    icon: HomeIcon,
  },
  {
    slug: "roof-coating-repair",
    title: "Roof Coating & Repair",
    description: "Comprehensive roof coating and repair services with GE Enduris protection",
    icon: Shield,
  },
  {
    slug: "waterproofing",
    title: "Waterproofing",
    description: "Long-lasting waterproofing protection for homes and commercial properties",
    icon: Droplets,
  },
  {
    slug: "pressure-washing",
    title: "Pressure Washing",
    description: "Professional pressure washing to prepare surfaces and restore beauty",
    icon: Waves,
  },
  {
    slug: "drywall-repair",
    title: "Drywall Repair",
    description: "Professional drywall repair and patching before painting",
    icon: Hammer,
  },
  {
    slug: "deck-staircase-repair",
    title: "Deck & Staircase Repair",
    description: "Restore and rebuild outdoor living spaces with quality craftsmanship",
    icon: Wrench,
  },
  {
    slug: "trim-molding",
    title: "Trim & Molding Painting",
    description: "Detail-oriented painting of baseboards, crown molding, and architectural elements",
    icon: Paintbrush,
  },
  {
    slug: "commercial-painting",
    title: "Commercial Painting",
    description: "Professional painting for offices, retail spaces, restaurants, and industrial facilities",
    icon: Building2,
  },
  {
    slug: "venetian-plaster",
    title: "Venetian Plaster",
    description: "Authentic Old World Venetian plaster application — timeless, luxurious, and unique to every surface",
    icon: Palette,
  },
  {
    slug: "lime-wash",
    title: "Lime Wash",
    description: "Traditional lime wash finishes for a soft, breathable, textured look on interior and exterior walls",
    icon: Layers,
  },
];

export default function Services() {
  const canonicalUrl = "https://danispainting.net/services";
  useEffect(() => {
    document.title = "Our Services | Danis Painting Co. Santa Barbara";
    
    // Add meta keywords
    let metaKeywordsTag = document.querySelector('meta[name="keywords"]');
    if (!metaKeywordsTag) {
      metaKeywordsTag = document.createElement('meta');
      metaKeywordsTag.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywordsTag);
    }
    metaKeywordsTag.setAttribute('content', getMetaKeywords('services'));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <CanonicalTag url={canonicalUrl} />
      <OpenGraphTags
        title="Our Services | Danis Painting Co. Santa Barbara"
        description="Comprehensive painting and waterproofing solutions for residential and commercial properties. Cabinet painting, interior/exterior painting, roof coating, waterproofing, pressure washing, and more."
        image="https://danispainting.net/logo.png"
        url="https://danispainting.net/services"
        type="website"
      />

      <Header />

      {/* Hero Section */}
      <section className="relative text-white pt-32 pb-16 md:pt-36 md:pb-20 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #0a1628 100%)' }}>
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: 'url(https://d2xsxph8kpxj0f.cloudfront.net/310519663313033271/eUvaV5v4nLRJmrJ6AKc5ww/hero-bg_5849f6b9_e05061fa.webp)' }}></div>
        <div className="container px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Our Services
            </h1>
            <p className="text-xl md:text-2xl text-white/90">
              Comprehensive painting and waterproofing solutions for residential and commercial properties throughout Santa Barbara, Ventura, and Los Angeles counties.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pt-12 pb-16">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <Link
                  key={service.slug}
                  href={`/service/${service.slug}`}
                  className="group bg-white border border-gray-200 rounded-lg p-6 hover:border-[#1e3a8a] hover:shadow-xl transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#1e3a8a]/10 rounded-lg flex items-center justify-center group-hover:bg-[#1e3a8a] transition-colors">
                      <Icon className="w-6 h-6 text-[#1e3a8a] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#1e3a8a] mb-2 group-hover:text-[#0066cc] transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {service.description}
                      </p>
                      <div className="mt-4 text-[#0066cc] font-medium group-hover:translate-x-2 transition-transform inline-block">
                        Learn More →
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-gray-50">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Why Choose Danis Painting Co.?
            </h2>
            <p className="text-lg text-gray-600">
              Over 30 years of exceptional quality and craftsmanship serving Southern California
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Licensed & Insured</h3>
              <p className="text-gray-600">
                Fully licensed and insured professionals you can trust with your property.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Premium Materials</h3>
              <p className="text-gray-600">
                We use only the highest quality paints and materials for lasting results.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Expert Craftsmanship</h3>
              <p className="text-gray-600">
                Meticulous attention to detail and professional workmanship on every project.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Free Estimates</h3>
              <p className="text-gray-600">
                Transparent pricing with detailed free estimates for all services.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Clean Worksites</h3>
              <p className="text-gray-600">
                We maintain clean, organized worksites and protect your property throughout.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-bold text-[#1e3a8a] mb-3">Customer Satisfaction</h3>
              <p className="text-gray-600">
                5-star rated service with hundreds of satisfied customers across Southern California.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <GoogleReviews maxReviews={3} />

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#1e3a8a] to-[#0066cc] text-white">
        <div className="container px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact Danis Painting Co. today for a free estimate and consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:805-403-8727" onClick={trackPhoneCall}
              className="inline-flex items-center justify-center px-8 py-4 bg-[#4caf30] text-white font-semibold rounded-lg hover:bg-[#3d9426] transition-colors"
            >
              Call: 805-403-8727
            </a>
            <button
              onClick={() => window.dispatchEvent(new Event('openChat'))}
              className="inline-flex items-center justify-center px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
            >
              Get Free Quote
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
